import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { useFeatureTogglingContext } from '@spacefill/shared/src/utils/FeatureToggling/FeatureToggling';
import { AvailableToggleFeatures } from '@spacefill/shared/src/utils/FeatureToggling/availableToggleFeatures';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';

import { getAdditionalRequestRoutes } from './routes/AdditionalRequestRoutes';
import { getAppointmentsRoutes } from './routes/AppointmentsRoutes';
import { getAutomationRoutes } from './routes/AutomationRoutes';
import { getDashboardRoutes } from './routes/DashboardRoutes';
import { getDocumentRoutes } from './routes/DocumentRoutes';
import { getIncidentAutoCreateRoutes } from './routes/IncidentAutoCreateRoutes';
import { getIncidentsRoutes } from './routes/IncidentsRoutes';
import { getIntegrationsRoutes } from './routes/IntegrationsRoutes';
import { getMasterItemRoutes } from './routes/MasterItemRoutes';
import { getOrderRoutes } from './routes/OrderRoutes';
import { getSettingsRoutes } from './routes/SettingsRoutes';
import { getStockSituationRoutes } from './routes/StockSituationRoutes';
import { getTransportsRoutes } from './routes/TransportRoutes';
import { getUserRoutes } from './routes/UserRoutes';
import { getWelcomePageRoutes } from './routes/WelcomePageRoutes';

const CompanySwitcherLoader = async () =>
    import('../layout/CompanySwitcherLoader').then((module) => ({
        Component: module.CompanySwitcherLoader,
    }));
export const getLoggedUserRoutes = (user, features) => {
    return [
        ...getWelcomePageRoutes(user, features),
        ...getDashboardRoutes(user),
        ...getOrderRoutes(user),
        ...getStockSituationRoutes(user),
        ...getTransportsRoutes(user),
        ...getAppointmentsRoutes(user),
        ...getDocumentRoutes(user),
        ...getIncidentAutoCreateRoutes(user),
        ...getIncidentsRoutes(user),
        ...getMasterItemRoutes(user),
        ...getIntegrationsRoutes(user),
        ...getAdditionalRequestRoutes(user),
        ...getAutomationRoutes(user, features),
        ...getSettingsRoutes(user, features),
        ...getUserRoutes(user, features),

        // Special route
        {
            path: '/admin/switch-company',
            lazy: CompanySwitcherLoader,
        },
        // Backward compat: redirect old /logistic-management/* URLs to /*
        {
            path: '/logistic-management/*',
            element: <LogisticManagementPrefixRedirect />,
        },
        //  Default
        {
            path: '*',
            element: <DefaultNavigateLoggerUser />,
        },
    ];
};

const LogisticManagementPrefixRedirect = () => {
    const { '*': splat } = useParams();
    const [searchParams] = useSearchParams();
    const search = searchParams.size > 0 ? `?${searchParams.toString()}` : '';
    return (
        <Navigate
            to={`/${splat}${search}`}
            replace
        />
    );
};

const DefaultNavigateLoggerUser = () => {
    // This is the only case when we need the useUserContext out from the RouterContext,
    // because of login redirection.
    const { user } = useUserContext();
    const { features } = useFeatureTogglingContext();
    const configurations = user?.customerWarehouseConfigurations ?? [];

    if (user.role === 'PROVIDER') {
        if (features?.[AvailableToggleFeatures.welcome_page]) {
            return <Navigate to='/' />;
        }
        if (user?.isTransportOrdersManagementEnabled && configurations.length === 0) {
            return <Navigate to='/transports/' />;
        }
        if (!user?.features?.disableAnalytics) {
            return <Navigate to='/analytics/' />;
        }
        if (configurations.length > 0) {
            return <Navigate to='/exit-orders/' />;
        }
        return <Navigate to='/profile/' />;
    }
    if (user.role === 'SHIPPER') {
        if (features?.[AvailableToggleFeatures.welcome_page]) {
            return <Navigate to='/' />;
        }
        if (!user?.features?.disableAnalytics) {
            return <Navigate to='/analytics/' />;
        }
        if (configurations.length > 0) {
            return <Navigate to='/exit-orders/' />;
        }
        return <Navigate to='/profile/' />;
    }
    if (user.role === 'CARRIER') {
        return <Navigate to={'/transports/'} />;
    }
    return <Navigate to='/profile/' />;
};
